import { useId } from 'react';
import { Empty, Space, Typography, theme } from 'antd';
import logoGray from '../../assets/branding/logo-gray-v2.svg';

const { Text } = Typography;

type SportEmptyImageProps = {
  size?: string | number;
};

interface StyledEmptyProps {
  title?: string;
  description?: string;
  imageSize?: string | number;
}

export function SportEmptyImage({ size = 'clamp(72px, 10vw, 112px)' }: SportEmptyImageProps) {
  const patternId = useId().replace(/:/g, '');
  const netPatternId = `${patternId}-empty-net`;
  const stripePatternId = `${patternId}-empty-stripes`;
  const {
    token: {
      colorBgContainer,
      colorBorder,
      colorBorderSecondary,
      colorFillQuaternary,
      colorTextTertiary,
      colorTextQuaternary,
    },
  } = theme.useToken();

  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: 'clamp(16px, 2vw, 24px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'transparent',
        color: colorTextTertiary,
        margin: '0 auto',
      }}
    >
      <svg
        aria-hidden="true"
        focusable="false"
        width="86%"
        height="62%"
        viewBox="0 0 900 600"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <pattern id={netPatternId} width="25" height="25" patternUnits="userSpaceOnUse">
            <path d="M25 0H0V25" fill="none" stroke={colorBorderSecondary} strokeWidth="1" />
          </pattern>
          <pattern id={stripePatternId} width="40" height="40" patternUnits="userSpaceOnUse">
            <rect width="20" height="40" fill={colorTextTertiary} />
            <rect x="20" width="20" height="40" fill={colorBgContainer} />
          </pattern>
        </defs>
        <rect width="900" height="600" rx="48" fill="transparent" />
        <path
          d="M80 120 Q450 150 820 120 L820 530 Q450 560 80 530 Z"
          fill={`url(#${netPatternId})`}
          stroke={colorBorderSecondary}
          strokeWidth="4"
        />
        <polygon
          points="50,150 80,120 80,530 50,500"
          fill={`url(#${netPatternId})`}
          stroke={colorBorderSecondary}
          strokeWidth="4"
        />
        <polygon
          points="820,120 850,150 850,500 820,530"
          fill={`url(#${netPatternId})`}
          stroke={colorBorderSecondary}
          strokeWidth="4"
        />
        <polyline
          points="80,120 50,150 50,500 80,530"
          stroke={colorTextQuaternary}
          strokeWidth="18"
          strokeLinejoin="round"
        />
        <polyline
          points="820,120 850,150 850,500 820,530"
          stroke={colorTextQuaternary}
          strokeWidth="18"
          strokeLinejoin="round"
        />
        <line x1="80" y1="530" x2="820" y2="530" stroke={colorTextQuaternary} strokeWidth="18" />
        <rect x="40" y="100" width="40" height="440" fill={`url(#${stripePatternId})`} stroke={colorBorder} strokeWidth="4" />
        <rect x="820" y="100" width="40" height="440" fill={`url(#${stripePatternId})`} stroke={colorBorder} strokeWidth="4" />
        <rect x="80" y="100" width="740" height="40" fill={`url(#${stripePatternId})`} stroke={colorBorder} strokeWidth="4" />
        <circle cx="52" cy="112" r="7" fill={colorFillQuaternary} stroke={colorBorder} strokeWidth="3" />
        <circle cx="848" cy="112" r="7" fill={colorFillQuaternary} stroke={colorBorder} strokeWidth="3" />
        <circle cx="52" cy="528" r="7" fill={colorFillQuaternary} stroke={colorBorder} strokeWidth="3" />
        <circle cx="848" cy="528" r="7" fill={colorFillQuaternary} stroke={colorBorder} strokeWidth="3" />
      </svg>
    </div>
  );
}

export function AppLogoEmptyImage({ size = 'clamp(88px, 12vw, 132px)' }: SportEmptyImageProps) {
  return (
    <div
      style={{
        width: size,
        height: size,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto',
      }}
    >
      <img
        src={logoGray}
        alt=""
        aria-hidden="true"
        style={{
          display: 'block',
          maxWidth: '100%',
          maxHeight: '100%',
          objectFit: 'contain',
          opacity: 0.38,
        }}
      />
    </div>
  );
}

export function StyledEmpty({
  title = 'No data yet',
  description = 'There is nothing to show here right now.',
  imageSize,
}: StyledEmptyProps) {
  const {
    token: { colorText, colorTextSecondary },
  } = theme.useToken();

  return (
    <Empty
      className="ys-empty-state"
      image={<AppLogoEmptyImage size={imageSize} />}
      description={
        <Space direction="vertical" size={2}>
          <Text strong style={{ color: colorText }}>{title}</Text>
          {description ? <Text type="secondary" style={{ color: colorTextSecondary }}>{description}</Text> : null}
        </Space>
      }
    />
  );
}
